import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteConfig } from '@/mocks/siteConfig';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles.filter((a) => a.id !== id).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-medium text-foreground-900 mb-2">文章不存在</h2>
            <p className="text-foreground-500 mb-6">该文章可能已被删除或链接失效</p>
            <Link to="/" className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <header>
        <Navbar />
      </header>

      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '新闻资讯', to: '/article/a1' },
              { label: article.title },
            ]}
          />

          <article className="max-w-[900px] mx-auto px-4 md:px-6 py-10">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden">
              <div className="relative w-full h-64 md:h-80 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`九游娱乐${article.title.slice(0, 20)}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="p-6 md:p-10">
                <div className="flex items-center gap-4 mb-4 text-xs text-foreground-500 flex-wrap">
                  <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
                    {article.category}
                  </span>
                  <span>
                    <i className="ri-user-line mr-1"></i>
                    {article.author}
                  </span>
                  <span>
                    <i className="ri-calendar-line mr-1"></i>
                    {article.date}
                  </span>
                  <span>
                    <i className="ri-eye-line mr-1"></i>
                    {article.readCount} 阅读
                  </span>
                </div>

                <h1 className="text-xl md:text-3xl font-heading text-foreground-950 mb-4 leading-snug">
                  {article.title}
                </h1>

                <div className="flex items-center gap-2 mb-8 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs text-foreground-600 bg-background-100 border border-background-200">
                      {tag}
                    </span>
                  ))}
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed [&_h4]:text-lg [&_h4]:font-medium [&_h4]:text-foreground-900 [&_h4]:mt-8 [&_h4]:mb-3 [&_p]:mb-4 [&_p]:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-10">
                <h3 className="text-lg font-medium text-foreground-900 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-300"
                    >
                      <div className="relative w-full h-36 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          title={`九游娱乐${ra.title.slice(0, 15)}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
}